import {
  parseAdminBootstrap,
  parseAuditEvents,
  parseHumanObservationDecision,
  parseHumanObservationQueue,
  parsePlatformCustomer,
  parsePlatformReadiness,
  type AdminBootstrap,
  type HumanObservationDecision,
  type HumanObservationQueueItem,
  type PlatformAuditEvent,
  type PlatformReadiness
} from "./admin-model";
import type { PlatformCustomer } from "../../src/contracts/platform-admin";

export class AdminApiError extends Error {
  readonly status: number;
  readonly code?: string;

  constructor(status: number, message: string, code?: string) {
    super(message);
    this.name = "AdminApiError";
    this.status = status;
    if (code !== undefined) this.code = code;
  }
}

const readProblem = async (response: Response): Promise<Readonly<{ message: string; code?: string }>> => {
  try {
    const value = await response.json() as unknown;
    if (typeof value === "object" && value !== null) {
      const candidate = value as Record<string, unknown>;
      const message = typeof candidate.detail === "string" ? candidate.detail
        : typeof candidate.message === "string" ? candidate.message : undefined;
      const code = typeof candidate.code === "string" ? candidate.code
        : typeof candidate.type === "string" ? candidate.type.split("/").at(-1) : undefined;
      return { message: message ?? `The platform request failed (${response.status}).`, ...(code === undefined ? {} : { code }) };
    }
  } catch { /* A non-JSON error remains a bounded generic problem. */ }
  return { message: `The platform request failed (${response.status}).` };
};

const fetchJson = async (path: string, init: RequestInit = {}, acceptedErrorStatuses: readonly number[] = []): Promise<unknown> => {
  const response = await fetch(path, {
    ...init,
    credentials: "same-origin",
    headers: { accept: "application/json", ...init.headers }
  });
  if (!response.ok && !acceptedErrorStatuses.includes(response.status)) {
    const problem = await readProblem(response);
    throw new AdminApiError(response.status, problem.message, problem.code);
  }
  try { return await response.json() as unknown; }
  catch { throw new AdminApiError(response.status, "The platform returned an unreadable response."); }
};

export const loadBootstrap = async (): Promise<AdminBootstrap> =>
  parseAdminBootstrap(await fetchJson("/api/v1/platform/bootstrap"));

export const loadCustomer = async (customerKey: string): Promise<PlatformCustomer> =>
  parsePlatformCustomer(await fetchJson(`/api/v1/platform/customers/${encodeURIComponent(customerKey)}`));

export const loadAuditEvents = async (limit = 100): Promise<readonly PlatformAuditEvent[]> =>
  parseAuditEvents(await fetchJson(`/api/v1/platform/audit?limit=${Math.min(Math.max(Math.trunc(limit), 1), 100)}`));

export const loadHumanObservationQueue = async (limit = 100): Promise<readonly HumanObservationQueueItem[]> =>
  parseHumanObservationQueue(await fetchJson(`/api/v1/platform/human-observation/queue?limit=${Math.min(Math.max(Math.trunc(limit), 1), 100)}`));

export const loadPlatformReadiness = async (): Promise<PlatformReadiness> =>
  parsePlatformReadiness(await fetchJson("/api/v1/platform/readiness", {}, [503]));

type SupportOperation = "approve" | "revoke" | "resolve";

const stableSupportIdempotencyKey = async (
  item: HumanObservationQueueItem,
  operation: SupportOperation
): Promise<string> => {
  const canonical = [
    "phenera-admin-support-v1",
    operation,
    item.tenant_id,
    item.instance_id,
    item.observation_request_id,
    item.route_sha256,
    item.scope_sha256 ?? ""
  ].join("|");
  const digest = new Uint8Array(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(canonical)));
  return `admin-support-v1-${Array.from(digest, (byte) => byte.toString(16).padStart(2, "0")).join("")}`;
};

export const submitHumanObservationDecision = async (
  item: HumanObservationQueueItem,
  operation: SupportOperation
): Promise<HumanObservationDecision> => {
  const body = operation === "approve"
    ? { expected_route_sha256: item.route_sha256, expected_scope_sha256: item.scope_sha256 }
    : { outcome: operation === "revoke" ? "owner_revoked" : "observation_resolved" };
  const path = [
    "/api/v1/platform/human-observation",
    encodeURIComponent(item.tenant_id),
    encodeURIComponent(item.instance_id),
    encodeURIComponent(item.observation_request_id),
    operation
  ].join("/");
  const idempotencyKey = await stableSupportIdempotencyKey(item, operation);
  return parseHumanObservationDecision(await fetchJson(path, {
    method: "POST",
    headers: { "content-type": "application/json", "idempotency-key": idempotencyKey },
    body: JSON.stringify(body)
  }));
};
